import { useCallback, useState, useEffect } from 'react';
import { viewportManager } from '../utils/telegram';
import { shouldUseFullscreen, getDeviceType } from '../utils/deviceUtils';

export function useTelegram() {
  const [isReady, setIsReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const [fullscreenRequested, setFullscreenRequested] = useState(false);
  
  // Check if Telegram WebApp is available in the window object
  useEffect(() => {
    const checkTelegramWebApp = () => {
      const isTelegramAvailable = Boolean(window.Telegram?.WebApp);
      setIsReady(isTelegramAvailable);
      
      if (isTelegramAvailable && !isReady && !fullscreenRequested) {
        // console.log('Telegram WebApp is available');
        // Notify Telegram that our WebApp is ready
        window.Telegram.WebApp.ready();
        
        // Request fullscreen mode using official API (only once and only for mobile devices)
        setTimeout(() => {
          setFullscreenRequested(true);
          
          if (shouldUseFullscreen()) {
            console.log(`📱 Device type: ${getDeviceType()}, requesting fullscreen for mobile device`);
            viewportManager.requestFullscreen();
          } else {
            console.log(`💻 Device type: ${getDeviceType()}, skipping fullscreen request for non-mobile device`);
          }
          
          setIsFullscreen(viewportManager.isFullscreen());
          setViewportHeight(viewportManager.getViewportHeight());
        }, 100);
      }
    };
    
    // Check immediately
    checkTelegramWebApp();
    
    // Also add event listener in case it loads later
    window.addEventListener('telegram-web-app-ready', checkTelegramWebApp);
    
    return () => {
      window.removeEventListener('telegram-web-app-ready', checkTelegramWebApp);
    };
  }, [isReady, fullscreenRequested]);
  
  // Listen to viewport and fullscreen changes
  useEffect(() => {
    const handleViewportChange = () => {
      setViewportHeight(viewportManager.getViewportHeight());
      console.log('📱 Viewport changed:', {
        height: viewportManager.getViewportHeight(),
        stableHeight: viewportManager.getViewportStableHeight()
      });
    };
    
    const handleFullscreenChange = () => {
      setIsFullscreen(viewportManager.isFullscreen());
      console.log('🔄 Fullscreen state changed:', viewportManager.isFullscreen());
    };
    
    if (isReady) {
      viewportManager.onViewportChanged(handleViewportChange);
      viewportManager.onFullscreenChanged(handleFullscreenChange);
      
      return () => {
        viewportManager.offViewportChanged(handleViewportChange);
        viewportManager.offFullscreenChanged(handleFullscreenChange);
      };
    }
  }, [isReady]);
  
  // Get WebApp object from window
  const tgWebApp = window.Telegram?.WebApp || null;
  
  // Get user data
  const user = tgWebApp?.initDataUnsafe?.user || null;
  
  // Fullscreen controls using official Telegram API
  const fullscreenControls = {
    // Request fullscreen mode
    requestFullscreen: useCallback(() => {
      const success = viewportManager.requestFullscreen();
      if (success) {
        setIsFullscreen(viewportManager.isFullscreen());
      }
      return success;
    }, []),
    
    // Exit fullscreen mode
    exitFullscreen: useCallback(() => {
      const success = viewportManager.exitFullscreen();
      if (success) {
        setIsFullscreen(viewportManager.isFullscreen());
      }
      return success;
    }, []),
    
    // Toggle fullscreen mode
    toggleFullscreen: useCallback(() => {
      const success = viewportManager.toggleFullscreen();
      setIsFullscreen(viewportManager.isFullscreen());
      return success;
    }, []),
    
    // Check if in fullscreen mode
    isFullscreen: useCallback(() => {
      return viewportManager.isFullscreen();
    }, []),
    
    // Expand to full height (fallback)
    expand: useCallback(() => {
      const success = viewportManager.expand();
      setIsFullscreen(viewportManager.isExpanded());
      return success;
    }, []),
    
    // Check if expanded
    isExpanded: useCallback(() => {
      return viewportManager.isExpanded();
    }, []),
    
    // Get current viewport info
    getViewportInfo: useCallback(() => {
      return {
        height: viewportManager.getViewportHeight(),
        stableHeight: viewportManager.getViewportStableHeight(),
        isExpanded: viewportManager.isExpanded(),
        isFullscreen: viewportManager.isFullscreen()
      };
    }, [])
  };
  
  // Helper functions for WebApp features
  const closeApp = useCallback(() => {
    if (tgWebApp) {
      tgWebApp.close();
    } else {
      console.log('CloseApp called but Telegram WebApp is not available');
    }
  }, [tgWebApp]);
  
  const showPopup = useCallback((title, message, buttons) => {
    if (tgWebApp) {
      tgWebApp.showPopup({
        title,
        message,
        buttons
      });
    } else {
      alert(`${title}\n\n${message}`);
    }
  }, [tgWebApp]);
  
  const showAlert = useCallback((message) => {
    if (tgWebApp) {
      tgWebApp.showAlert(message);
    } else {
      alert(message);
    }
  }, [tgWebApp]);
  
  const showConfirm = useCallback((message, callback) => {
    if (tgWebApp) {
      tgWebApp.showConfirm(message, callback);
    } else {
      const result = window.confirm(message);
      if (callback) callback(result);
    }
  }, [tgWebApp]);
  
  // BackButton controls
  const backButton = {
    show: useCallback(() => {
      if (tgWebApp?.BackButton) {
        tgWebApp.BackButton.show();
      }
    }, [tgWebApp]),
    
    hide: useCallback(() => {
      if (tgWebApp?.BackButton) {
        tgWebApp.BackButton.hide();
      }
    }, [tgWebApp]),
    
    onClick: useCallback((callback) => {
      if (tgWebApp?.BackButton) {
        tgWebApp.BackButton.onClick(callback);
      }
    }, [tgWebApp])
  };
  
  // MainButton controls
  const mainButton = {
    show: useCallback((text) => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.setText(text);
        tgWebApp.MainButton.show();
      }
    }, [tgWebApp]),
    
    hide: useCallback(() => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.hide();
      }
    }, [tgWebApp]),
    
    onClick: useCallback((callback) => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.onClick(callback);
      }
    }, [tgWebApp]),
    
    offClick: useCallback((callback) => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.offClick(callback);
      }
    }, [tgWebApp]),
    
    enable: useCallback(() => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.enable();
      }
    }, [tgWebApp]),
    
    disable: useCallback(() => {
      if (tgWebApp?.MainButton) {
        tgWebApp.MainButton.disable();
      }
    }, [tgWebApp])
  };
  
  return {
    user,
    tgData: tgWebApp?.initDataUnsafe || null,
    isReady,
    isFullscreen,
    viewportHeight,
    fullscreenControls,
    closeApp,
    showPopup,
    showAlert,
    showConfirm,
    backButton,
    mainButton,
    tgWebApp
  };
} 